/**
 * Base44 Client - Unified API
 *
 * Base44 platform API'sini taklit eden birleşik istemci.
 * Farklı backend'ler arasında kolayca geçiş yapabilirsiniz.
 *
 * Kullanım:
 * import { base44, setAdapter, ADAPTERS } from '@/api/base44Client';
 *
 * // LocalStorage (varsayılan - demo/geliştirme için)
 * setAdapter(ADAPTERS.LOCAL_STORAGE);
 *
 * // Supabase (production için)
 * import { initSupabase } from '@/api/adapters/SupabaseAdapter';
 * initSupabase('YOUR_URL', 'YOUR_KEY');
 * setAdapter(ADAPTERS.SUPABASE);
 *
 * // REST API (custom backend için)
 * import { setAuthToken } from '@/api/adapters/_RestApiAdapter';
 * setAuthToken('your-jwt-token');
 * setAdapter(ADAPTERS.REST_API);
 *
 * // Kullanım (tüm adapter'lerde aynı)
 * const leads = await base44.entities.Lead.list();
 * const newLead = await base44.entities.Lead.create({ name: 'Test' });
 */

import { LocalStorageAdapter } from './adapters/_LocalStorageAdapter';
import { SupabaseAdapter, initSupabase } from './adapters/SupabaseAdapter';
import { RestApiAdapter } from './adapters/_RestApiAdapter';
import { MockIntegrations } from './integrations/_MockIntegrations';
import { RealIntegrations } from './integrations/RealIntegrations';

/**
 * Kullanılabilir adapter'ler
 */
export const ADAPTERS = {
  LOCAL_STORAGE: 'localStorage',
  SUPABASE: 'supabase',
  REST_API: 'restApi',
};

/**
 * Kullanılabilir entegrasyon modları
 */
export const INTEGRATION_MODES = {
  MOCK: 'mock',
  REAL: 'real',
};

// Aktif adapter ve entegrasyon modu
let currentAdapter = ADAPTERS.LOCAL_STORAGE;
let currentIntegrationMode = INTEGRATION_MODES.MOCK;

/**
 * Adapter'ı değiştirir
 */
export function setAdapter(adapter) {
  if (!Object.values(ADAPTERS).includes(adapter)) {
    throw new Error(`Invalid adapter: ${adapter}. Use one of: ${Object.values(ADAPTERS).join(', ')}`);
  }
  currentAdapter = adapter;
  console.log(`[base44] Adapter set to: ${adapter}`);
}

/**
 * Entegrasyon modunu değiştirir
 */
export function setIntegrationMode(mode) {
  if (!Object.values(INTEGRATION_MODES).includes(mode)) {
    throw new Error(`Invalid integration mode: ${mode}. Use one of: ${Object.values(INTEGRATION_MODES).join(', ')}`);
  }
  currentIntegrationMode = mode;
  console.log(`[base44] Integration mode set to: ${mode}`);
}

/**
 * Aktif adapter'ı döndürür
 */
function getActiveAdapter() {
  switch (currentAdapter) {
    case ADAPTERS.SUPABASE:
      return SupabaseAdapter;
    case ADAPTERS.REST_API:
      return RestApiAdapter;
    case ADAPTERS.LOCAL_STORAGE:
    default:
      return LocalStorageAdapter;
  }
}

/**
 * Aktif entegrasyonları döndürür
 */
function getActiveIntegrations() {
  switch (currentIntegrationMode) {
    case INTEGRATION_MODES.REAL:
      return RealIntegrations;
    case INTEGRATION_MODES.MOCK:
    default:
      return MockIntegrations;
  }
}

/**
 * Proxy handler - entity method çağrılarını yakalar
 */
const entitiesProxy = new Proxy({}, {
  get(target, entityName) {
    const adapter = getActiveAdapter();
    const entity = adapter.entities[entityName];

    if (!entity) {
      console.warn(`[base44] Entity not found: ${entityName}`);
      return undefined;
    }

    return entity;
  },
});

/**
 * Integrations proxy
 */
const integrationsProxy = new Proxy({}, {
  get(target, serviceName) {
    const integrations = getActiveIntegrations();
    return integrations[serviceName];
  },
});

/**
 * Users proxy
 */
const usersProxy = new Proxy({}, {
  get(target, prop) {
    const adapter = getActiveAdapter();
    if (!adapter.users) {
       console.warn('[base44] Users management not supported by current adapter');
       return () => {};
    }
    return adapter.users[prop];
  },
});

/**
 * Ana base44 client
 */
export const base44 = {
  /**
   * Entity erişimi
   * base44.entities.Lead.list()
   * base44.entities.Task.create({ ... })
   */
  entities: entitiesProxy,

  /**
   * Entegrasyon erişimi
   * base44.integrations.Core.InvokeLLM({ ... })
   * base44.integrations.Core.SendEmail({ ... })
   */
  integrations: integrationsProxy,
  
  /**
   * User management
   */
  users: usersProxy,

  /**
   * Yardımcı methodlar
   */
  getAdapter: () => currentAdapter,
  getIntegrationMode: () => currentIntegrationMode,
  setAdapter,
  setIntegrationMode,

  /**
   * LocalStorage verilerini export eder
   */
  exportData: () => {
    if (currentAdapter === ADAPTERS.LOCAL_STORAGE) {
      return LocalStorageAdapter.exportData();
    }
    throw new Error('Export only available for LocalStorage adapter');
  },

  /**
   * LocalStorage'a veri import eder
   */
  importData: (data) => {
    if (currentAdapter === ADAPTERS.LOCAL_STORAGE) {
      return LocalStorageAdapter.importData(data);
    }
    throw new Error('Import only available for LocalStorage adapter');
  },

  /**
   * LocalStorage verilerini temizler
   */
  clearData: () => {
    if (currentAdapter === ADAPTERS.LOCAL_STORAGE) {
      return LocalStorageAdapter.clearAll();
    }
    throw new Error('Clear only available for LocalStorage adapter');
  },
};

// Auto-init: Environment'a göre adapter seç
if (typeof window !== 'undefined') {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL;

  // Supabase config varsa Supabase adapter'ı kullan
  if (supabaseUrl && supabaseKey) {
    console.log('[base44] Supabase config detected, initializing...');
    initSupabase(supabaseUrl, supabaseKey);
    setAdapter(ADAPTERS.SUPABASE);
  } else if (apiBaseUrl) {
    // REST API config varsa REST adapter'ı kullan
    setAdapter(ADAPTERS.REST_API);
  }
  // Aksi halde LocalStorage kullanılır (varsayılan)

  // OpenAI key varsa real integrations kullan
  const openaiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  if (openaiKey) {
    setIntegrationMode(INTEGRATION_MODES.REAL);
  }
}

export default base44;
